import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  customCollectionsAPI,
  CustomCollection,
  BibleVerse,
} from "../services/api";

const EditCustomCollection: React.FC = () => {
  const { collectionId } = useParams<{ collectionId: string }>();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const [collection, setCollection] = useState<CustomCollection | null>(null);
  const [selectedVerses, setSelectedVerses] = useState<BibleVerse[]>([]);
  const [availableVerses, setAvailableVerses] = useState<BibleVerse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Editable collection fields
  const [collectionName, setCollectionName] = useState("");
  const [collectionDescription, setCollectionDescription] = useState("");
  const [collectionExerciseType, setCollectionExerciseType] = useState<
    "clozes" | "verbs" | "both"
  >("both");

  // Filters
  const [selectedBook, setSelectedBook] = useState<string>("All");
  const [selectedChapter, setSelectedChapter] = useState<number | string>(
    "all"
  );
  const [availableBooks, setAvailableBooks] = useState<string[]>([]);
  const [chapters, setChapters] = useState<{ book: string; chapter: number }[]>(
    []
  );

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedBook) {
      fetchChapters();
    }
  }, [selectedBook]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedChapter) {
      fetchAvailableVerses();
    }
  }, [selectedChapter, selectedBook]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch collection and its verses
      const collectionResponse = await customCollectionsAPI.getCollection(
        parseInt(collectionId!),
        getAccessTokenSilently
      );

      const collectionData = collectionResponse.data.collection;
      setCollection(collectionData);
      setSelectedVerses(collectionResponse.data.verses || []);

      // Initialize editable fields
      setCollectionName(collectionData.name || "");
      setCollectionDescription(collectionData.description || "");
      setCollectionExerciseType(collectionData.exercise_type || "both");

      // Fetch all available books
      const versesResponse = await customCollectionsAPI.getBibleVerses();
      const verses = versesResponse.data.verses || [];
      
      // Define canonical biblical order for New Testament books
      const canonicalOrder = [
        "Matthew", "Mark", "Luke", "John",
        "Acts",
        "Romans", "1 Corinthians", "2 Corinthians", "Galatians", 
        "Ephesians", "Philippians", "Colossians", 
        "1 Thessalonians", "2 Thessalonians", 
        "1 Timothy", "2 Timothy", "Titus", "Philemon",
        "Hebrews", "James", "1 Peter", "2 Peter", 
        "1 John", "2 John", "3 John", "Jude",
        "Revelation"
      ];
      
      const uniqueBooks = (Array.from(
        new Set(verses.map((v: BibleVerse) => v.book))
      ) as string[]).sort((a, b) => {
        const indexA = canonicalOrder.indexOf(a);
        const indexB = canonicalOrder.indexOf(b);
        // If book not found in canonical order, put it at the end
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
      setAvailableBooks(uniqueBooks);

      // Fetch chapters for all books
      await fetchChapters();
    } catch (err: any) {
      setError("Failed to load collection data.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchChapters = async () => {
    try {
      if (selectedBook === "All") {
        // Fetch all chapters from all books
        const allChapters: { book: string; chapter: number }[] = [];
        for (const book of availableBooks) {
          const chaptersResponse = await customCollectionsAPI.getChapters(book);
          const bookChapters = chaptersResponse.data.chapters.map(
            (ch: number) => ({
              book,
              chapter: ch,
            })
          );
          allChapters.push(...bookChapters);
        }
        setChapters(allChapters);
      } else {
        // Fetch chapters for selected book
        const chaptersResponse = await customCollectionsAPI.getChapters(
          selectedBook
        );
        const bookChapters = chaptersResponse.data.chapters.map(
          (ch: number) => ({
            book: selectedBook,
            chapter: ch,
          })
        );
        setChapters(bookChapters);
      }

      // Reset chapter selection to "all" when book changes
      setSelectedChapter("all");
    } catch (err: any) {
      console.error("Failed to load chapters:", err);
    }
  };

  const fetchAvailableVerses = async () => {
    try {
      if (selectedChapter === "all") {
        // Fetch all verses for the selected book (or all books)
        const versesResponse =
          selectedBook === "All"
            ? await customCollectionsAPI.getBibleVerses()
            : await customCollectionsAPI.getBibleVerses(selectedBook);
        setAvailableVerses(versesResponse.data.verses || []);
      } else {
        // Fetch verses for specific chapter
        const [book, chapter] =
          typeof selectedChapter === "string" && selectedChapter.includes("-")
            ? selectedChapter.split("-")
            : [selectedBook, selectedChapter];

        const versesResponse = await customCollectionsAPI.getBibleVerses(
          book,
          parseInt(chapter as string)
        );
        setAvailableVerses(versesResponse.data.verses || []);
      }
    } catch (err: any) {
      console.error("Failed to load verses:", err);
    }
  };

  const handleAddVerse = async (verse: BibleVerse) => {
    try {
      await customCollectionsAPI.addVerses(
        parseInt(collectionId!),
        [verse.id],
        getAccessTokenSilently
      );

      // Add to selected verses
      setSelectedVerses([...selectedVerses, verse]);
    } catch (err: any) {
      setError("Failed to add verse.");
      console.error(err);
    }
  };

  const handleRemoveVerse = async (verseId: number) => {
    try {
      await customCollectionsAPI.removeVerse(
        parseInt(collectionId!),
        verseId,
        getAccessTokenSilently
      );

      // Remove from selected verses
      setSelectedVerses(selectedVerses.filter((v) => v.id !== verseId));
    } catch (err: any) {
      setError("Failed to remove verse.");
      console.error(err);
    }
  };

  const handleAddAll = async () => {
    try {
      // Filter out verses that are already selected
      const versesToAdd = availableVerses.filter(
        (verse) => !isVerseSelected(verse.id)
      );

      if (versesToAdd.length === 0) {
        return; // All verses already selected
      }

      const verseIds = versesToAdd.map((v) => v.id);

      await customCollectionsAPI.addVerses(
        parseInt(collectionId!),
        verseIds,
        getAccessTokenSilently
      );

      // Add all new verses to selected verses
      setSelectedVerses([...selectedVerses, ...versesToAdd]);
    } catch (err: any) {
      setError("Failed to add verses.");
      console.error(err);
    }
  };

  const isVerseSelected = (verseId: number) => {
    return selectedVerses.some((v) => v.id === verseId);
  };

  const handleSaveCollectionDetails = async () => {
    try {
      setSaving(true);
      setError("");

      await customCollectionsAPI.updateCollection(
        parseInt(collectionId!),
        {
          name: collectionName,
          description: collectionDescription,
          isPublic: collection?.is_public || false,
          exerciseType: collectionExerciseType,
        },
        getAccessTokenSilently
      );

      // Update local collection state
      if (collection) {
        setCollection({
          ...collection,
          name: collectionName,
          description: collectionDescription,
          exercise_type: collectionExerciseType,
        });
      }

      // Show success message briefly
      const successMsg = "Collection details saved successfully!";
      setError(""); // Clear any previous errors

      // You could add a success state here if desired
      console.log(successMsg);
    } catch (err: any) {
      setError("Failed to save collection details.");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="text-center">
        <p className="text-zinc-600 dark:text-zinc-400">
          Collection not found.
        </p>
        <button
          onClick={() => navigate("/decks")}
          className="mt-4 bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700"
        >
          Back to Collections
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate("/decks")}
          className="text-teal-600 dark:text-teal-400 hover:underline mb-2"
        >
          ← Back to Collections
        </button>
        <h1 className="text-3xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">
          Edit Collection
        </h1>
        <p className="text-zinc-600 dark:text-zinc-400">
          Update your collection details and select verses to include.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Collection Details Section */}
      <div className="bg-white dark:bg-zinc-800 rounded-lg shadow-md border border-zinc-200 dark:border-zinc-700 p-6 mb-6">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
          Collection Details
        </h2>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="collectionName"
              className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2"
            >
              Collection Name *
            </label>
            <input
              type="text"
              id="collectionName"
              value={collectionName}
              onChange={(e) => setCollectionName(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-transparent bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100"
              required
            />
          </div>

          <div>
            <label
              htmlFor="collectionDescription"
              className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2"
            >
              Description
            </label>
            <textarea
              id="collectionDescription"
              value={collectionDescription}
              onChange={(e) => setCollectionDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-transparent bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100"
            />
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSaveCollectionDetails}
              disabled={saving || !collectionName.trim()}
              className="bg-teal-600 hover:bg-teal-700 hover:border-teal-700 text-white px-6 py-2 rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving..." : "Save Details"}
            </button>
          </div>
        </div>
      </div>

      {/* Verse Selection Section */}
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
          Verse Selection
        </h2>
        <p className="text-zinc-600 dark:text-zinc-400 mt-1">
          Select verses to include in your collection. Only words tagged as
          nouns, verbs, adjectives, and adverbs will be used for cloze
          exercises.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Available Verses Panel */}
        <div className="bg-white dark:bg-zinc-800 rounded-lg shadow-md border border-zinc-200 dark:border-zinc-700 p-6">
          <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
            Available Verses
          </h2>

          {/* Book filter */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Select Book
            </label>
            <select
              value={selectedBook}
              onChange={(e) => setSelectedBook(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100"
            >
              <option value="All">All Books</option>
              {availableBooks.map((book) => (
                <option key={book} value={book}>
                  {book}
                </option>
              ))}
            </select>
          </div>

          {/* Chapter filter */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Select Chapter
            </label>
            <select
              value={selectedChapter}
              onChange={(e) => setSelectedChapter(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100"
            >
              <option value="all">All Chapters</option>
              {chapters.map((ch) => (
                <option
                  key={`${ch.book}-${ch.chapter}`}
                  value={`${ch.book}-${ch.chapter}`}
                >
                  {selectedBook === "All"
                    ? `${ch.book} Chapter ${ch.chapter}`
                    : `Chapter ${ch.chapter}`}
                </option>
              ))}
            </select>
          </div>

          {/* Add All button */}
          {availableVerses.length > 0 && (
            <div className="mb-4">
              <button
                onClick={handleAddAll}
                disabled={availableVerses.every((v) => isVerseSelected(v.id))}
                className={`w-full py-2 px-4 rounded-md transition-colors font-medium ${
                  availableVerses.every((v) => isVerseSelected(v.id))
                    ? "bg-zinc-300 dark:bg-zinc-600 text-zinc-500 dark:text-zinc-400 cursor-not-allowed"
                    : "bg-teal-600 text-white hover:bg-teal-700"
                }`}
              >
                {availableVerses.every((v) => isVerseSelected(v.id))
                  ? "All Verses Added"
                  : `Add All (${
                      availableVerses.filter((v) => !isVerseSelected(v.id))
                        .length
                    })`}
              </button>
            </div>
          )}

          {/* Verses list */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {availableVerses.map((verse) => {
              const selected = isVerseSelected(verse.id);
              return (
                <div
                  key={verse.id}
                  className={`p-3 rounded-lg border transition-colors ${
                    selected
                      ? "border-teal-500 bg-teal-50 dark:bg-teal-400/20"
                      : "border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-700"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">
                        {verse.reference}
                      </p>
                      <p className="text-sm text-zinc-600 dark:text-zinc-400 line-clamp-2">
                        {verse.full_text}
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        selected
                          ? handleRemoveVerse(verse.id)
                          : handleAddVerse(verse)
                      }
                      className={`ml-3 px-3 py-1 text-xs rounded transition-colors ${
                        selected
                          ? "bg-red-600 hover:bg-red-700 text-white"
                          : "bg-teal-600 text-white hover:bg-teal-700"
                      }`}
                    >
                      {selected ? "Remove" : "Add"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Selected Verses Panel */}
        <div className="bg-white dark:bg-zinc-800 rounded-lg shadow-md border border-zinc-200 dark:border-zinc-700 p-6">
          <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
            Selected Verses ({selectedVerses.length})
          </h2>

          {selectedVerses.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-zinc-400 dark:text-zinc-500 mb-2">
                <svg
                  className="mx-auto h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </div>
              <p className="text-zinc-500 dark:text-zinc-400">
                No verses selected yet
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {selectedVerses.map((verse) => (
                <div
                  key={verse.id}
                  className="p-3 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-700"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">
                        {verse.reference}
                      </p>
                      <p className="text-sm text-zinc-600 dark:text-zinc-400 line-clamp-2">
                        {verse.full_text}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemoveVerse(verse.id)}
                      className="ml-3 px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {selectedVerses.length > 0 && (
            <div className="mt-6 pt-6 border-t border-zinc-200 dark:border-zinc-700">
              <button
                onClick={() => navigate("/decks")}
                className="w-full bg-teal-600 text-white py-3 rounded-md hover:bg-teal-700 transition-colors font-medium"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditCustomCollection;
